import AtgGifLoader from '@common/AtgGifLoader';
import { Box } from '@atgfr8/atg-ui-tailwind';
import SectionCard from '@mobileComponents/SectionCard';
import { customerDetailsStore, getOrderHistory } from '@mobileStore/customer';
import { useParams } from '@solidjs/router';
import { formatDateOrNull } from '@utils/dateFormat';
import { throttle } from 'lodash';
import { createSignal, For, onMount, Show } from 'solid-js';

const OrderHistory = () => {
  const params = useParams();
  const [scrollContainer, setScrollContainer] = createSignal<HTMLDivElement>();

  onMount(() => {
    const customerId = params.id;
    if (customerId) {
      void getOrderHistory(customerId);
    }
  });

  const handleScroll = throttle(async () => {
    const el = scrollContainer();
    const { isLoading, hasMore } = customerDetailsStore.orderHistory;

    if (isLoading || !hasMore) return;

    if (!!el) {
      const bottomReached =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 20;

      if (bottomReached && params.id) {
        await getOrderHistory(params.id, true);
      }
    }
  }, 200);

  return (
    <Box
      ref={setScrollContainer}
      on:scroll={handleScroll}
      class="p-2 overflow-auto h-[calc(100vh-150px)]"
    >
      <Show
        when={
          !customerDetailsStore.orderHistory.isLoading ||
          customerDetailsStore.orderHistory.data.length > 0
        }
        fallback={
          <div class="flex items-center h-[calc(100vh-150px)] justify-center">
            <AtgGifLoader />
          </div>
        }
      >
        <SectionCard
          title={
            <>
              Order History
              {customerDetailsStore.orderHistory.data.length > 0 && (
                <span class="bg-[#b81a36] text-white rounded-full px-2 py-0.5 text-sm ml-2">
                  {customerDetailsStore.orderHistory.data.length}
                </span>
              )}
            </>
          }
        >
          <Box class="p-2">
            <For
              each={customerDetailsStore.orderHistory.data}
              fallback={
                <Show when={!customerDetailsStore.orderHistory.isLoading}>
                  <Box class="text-center py-6 text-gray-500">
                    <p class="text-[16px]">No Orders Found</p>
                  </Box>
                </Show>
              }
            >
              {(order) => (
                <Box class="border border-[#0000001F] rounded-sm mb-1">
                  <Box class="flex justify-between items-center bg-[#ECEFF1] border-b border-[#0000001F] p-2">
                    <span class="text-[14px]! font-medium! text-[#333]!">
                      #{order.orderId}
                    </span>
                    <span class="text-[14px]! font-medium! text-[#333]!">
                      {order.status}
                    </span>
                  </Box>
                  <Box class="flex justify-between items-start p-2 ">
                    <span class="text-[14px]! text-gray-600">
                      Ref#: {order.referenceNumber}
                    </span>
                    <span class="text-[14px]! text-gray-600 text-right ">
                      PO# {order.proNumber || '-'} / Load #{' '}
                      {order.numLoads || '-'}
                    </span>
                  </Box>
                  <hr class="mx-2! border-[#0000001F]" />
                  <Box class="flex justify-between items-center p-2">
                    <Box class="flex-1">
                      <span class="text-[14px]! font-medium! text-[#333]">
                        {order.originCity || '-'}
                        <Show when={order.originState}>
                          , {order.originState}
                        </Show>
                      </span>
                      <p class="text-[14px]! text-gray-500 mt-1">
                        {formatDateOrNull(order.pickUp) || '-'}
                      </p>
                    </Box>
                    <Box class="mx-4 flex items-center">
                      <svg
                        class="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="3"
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </Box>
                    <Box class="flex-1 text-right">
                      <span class="text-[14px]! font-medium! text-[#333]">
                        {order.destinationCity || '-'}
                        <Show when={order.destinationState}>
                          , {order.destinationState}
                        </Show>
                      </span>
                      <p class="text-[14px]! text-gray-500 mt-1">
                        {formatDateOrNull(order.dropoff) || '-'}
                      </p>
                    </Box>
                  </Box>
                </Box>
              )}
            </For>
            <Show
              when={
                customerDetailsStore.orderHistory.isLoading &&
                customerDetailsStore.orderHistory.data.length > 0
              }
            >
              <AtgGifLoader />
            </Show>
          </Box>
        </SectionCard>
      </Show>
    </Box>
  );
};

export default OrderHistory;
